import React, { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { TextField } from "@/components/common/Field";
import { ShieldCheck, ShieldAlert, ShieldX, Loader2, Users, Database, FileSearch } from "lucide-react";
import { cn } from "@/lib/utils";
import { SPRING, EASE } from "@/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEV = { HIGH: "text-danger bg-danger/10", MEDIUM: "text-warning bg-warning/10", LOW: "text-ink-2 bg-secondary" };
const LABEL = { ADVERSE_TO_EXISTING_CLIENT: "Adverse to existing client", CLIENT_WAS_ADVERSE_PARTY: "Client was adverse party", PARTY_IN_OTHER_MATTER: "Party in another matter", DUPLICATE_CLIENT_IDENTITY: "Duplicate client identity", OPPONENT_COUNSEL_IS_TEAM: "Opponent counsel is on the team", AUTHORITY_REQUIRED: "Representation authority missing" };

export function ConflictCheckStep({ form, set, check, setCheck }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [reason, setReason] = useState("");

  const run = async () => {
    setBusy(true); setErr("");
    try {
      const parties = [form.petitioner && { name: form.petitioner, role: "Petitioner" }, form.respondent && { name: form.respondent, role: "Respondent" }].filter(Boolean);
      const minVisible = new Promise((r) => setTimeout(r, 850));
      const [{ data }] = await Promise.all([
        axios.post(`${API}/intake/conflict-check`, { clientId: form.clientId, opponentCounsel: form.opponentCounsel, parties, authority: { signatoryName: form.signatoryName, instrument: form.instrument } }),
        minVisible,
      ]);
      setCheck(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Conflict check failed."); }
    finally { setBusy(false); }
  };
  const override = async () => {
    setBusy(true); setErr("");
    try { const { data } = await axios.post(`${API}/intake/conflict-check/${check.id}/override`, { reason, informedConsent: true }); setCheck(data); }
    catch (e) { setErr(e?.response?.data?.detail || "Override refused."); }
    finally { setBusy(false); }
  };

  const Icon = !check ? ShieldCheck : check.status === "CLEAR" ? ShieldCheck : check.status === "REVIEW" ? ShieldAlert : ShieldX;
  const tone = !check ? "text-ink-2" : check.status === "CLEAR" ? "text-success" : check.status === "REVIEW" ? "text-warning" : "text-danger";
  const cleared = check && (check.status === "CLEAR" || check.override);

  return (
    <div className="space-y-3" data-testid="conflict-check-step">
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Authorised Signatory (entity clients)" value={form.signatoryName || ""} onChange={(v) => set("signatoryName", v)} placeholder="e.g. Director — R. Sharma" testid="authority-signatory" />
        <TextField label="Authorising Instrument" value={form.instrument || ""} onChange={(v) => set("instrument", v)} placeholder="Board resolution / PoA / Partnership deed" testid="authority-instrument" />
      </div>
      <div className="flex items-center justify-between rounded-lg border border-line bg-surface p-3">
        <div className="flex items-center gap-2 text-sm">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={!check ? "none" : check.status + (check.override ? "-ov" : "")}
              initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={SPRING.micro}
            >
              <Icon className={cn("h-5 w-5", tone)} />
            </motion.span>
          </AnimatePresence>
          <span className={cn("font-medium", tone)} data-testid="conflict-status">{!check ? "Not yet run" : check.override ? `${check.status} · overridden by partner` : check.status}</span>
        </div>
        <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="run-conflict-check">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : check ? "Re-run check" : "Run conflict check"}</Button>
      </div>

      <AnimatePresence>
        {busy && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: EASE.standard }}
            className="overflow-hidden"
            data-testid="conflict-engine"
          >
            <div className="flex items-center justify-center gap-3 rounded-lg border border-copper/30 bg-copper-light/50 p-3 text-xs text-ink-2">
              {[{ Icon: Users, label: "Parties" }, { Icon: Database, label: "Client DB" }, { Icon: FileSearch, label: "Existing matters" }].map((s, i) => (
                <React.Fragment key={s.label}>
                  <motion.div
                    className="flex items-center gap-1.5"
                    animate={{ opacity: [0.35, 1, 0.35] }}
                    transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.18, ease: "easeInOut" }}
                  >
                    <s.Icon className="h-3.5 w-3.5 text-copper" /> {s.label}
                  </motion.div>
                  <span className="text-copper/50">→</span>
                </React.Fragment>
              ))}
              <span className="font-medium text-copper">Conflict Engine</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {err && <div className="text-xs text-danger" data-testid="conflict-error">{String(err)}</div>}
      {check && check.findings.length === 0 && <div className="text-xs text-success">No conflicts across clients, parties, matters or team. Representation authority is in order.</div>}
      {check && check.findings.length > 0 && (
        <ul className="space-y-1.5" data-testid="conflict-findings">
          {check.findings.map((f, i) => (
            <li key={i} className="flex items-start gap-2 rounded-lg border border-line p-2 text-xs">
              <span className={cn("rounded px-1.5 py-0.5 font-semibold", SEV[f.severity])}>{f.severity}</span>
              <div><div className="font-medium text-ink">{LABEL[f.type] || f.type} — {f.match}</div><div className="text-ink-2">{f.detail}{f.caseNumber ? ` (${f.caseNumber})` : ""}</div></div>
            </li>
          ))}
        </ul>
      )}
      {check && !cleared && (
        check.canOverride ? (
          <div className="space-y-2 rounded-lg border border-warning/40 bg-warning/5 p-3">
            <div className="text-xs font-medium text-ink">Partner override — recorded in the audit trail</div>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this engagement may proceed (informed consent obtained, unrelated matter, …)" data-testid="override-reason" />
            <Button size="sm" variant="outline" onClick={override} disabled={busy || reason.trim().length < 10} data-testid="override-submit">Record override</Button>
          </div>
        ) : <div className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-xs text-danger" data-testid="override-needed">A Managing Partner, Senior Advocate or Firm Admin must review and override before this matter can open.</div>
      )}
    </div>
  );
}
